import { useEffect, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import {
  useConfirmDriverCompliance,
  useDriverCompliance,
  useDriverDetail,
  useRemoveDriverCompliance,
  useUpdateDriver,
  useUpsertDriverCompliance,
  type DriverComplianceDocumentWithUrl,
} from '@/data/drivers';
import { useVehicles } from '@/data/vehicles';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Field, Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { formatDate } from '@/lib/format';
import { vehicleTypeLabel } from '@/lib/vehicle-labels';
import type {
  DriverComplianceDocType,
  DriverComplianceState,
  DriverEngagementType,
  DriverListEntry,
  FleetRole,
} from '@breaker/shared-types';
import type { InvitableBadge } from '@breaker/shared-schemas';

const COMPLIANCE_TYPES: Array<{ type: DriverComplianceDocType; label: string }> = [
  { type: 'goods_in_transit', label: 'Goods in transit' },
  { type: 'public_liability', label: 'Public liability' },
];

export const BADGE_LABEL: Record<FleetRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  dispatcher: 'Dispatcher',
  accountant: 'Accountant',
  driver: 'Driver',
};

/** Badge-set chips, strongest first. Driver carries the £ marker — it's the
 *  one billed badge. */
export function BadgeChips({ roles }: { roles: FleetRole[] }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {roles.map((r) => (
        <Badge key={r} tone={r === 'owner' ? 'info' : 'neutral'}>
          {BADGE_LABEL[r]}
          {r === 'driver' ? ' £' : ''}
        </Badge>
      ))}
    </span>
  );
}

export function EngagementLabel({ type }: { type: DriverEngagementType | null }) {
  if (type === 'subcontractor') return <span className="text-breaker-ink-2">Subcontractor</span>;
  if (type === 'employed') return <span className="text-breaker-ink-2">Employed</span>;
  return <span className="text-breaker-mute">Not set</span>;
}

export function ComplianceBadge({ state }: { state: DriverComplianceState }) {
  switch (state) {
    case 'ok':
      return <Badge tone="success">Cover OK</Badge>;
    case 'expiring_soon':
      return <Badge tone="info">Expiring soon</Badge>;
    case 'blocked':
      return <Badge tone="danger">Blocked</Badge>;
    default:
      return <span className="text-breaker-mute">{'—'}</span>;
  }
}

export function TeamMemberDetailPage() {
  const { memberId } = useParams({ from: '/authed/settings/team/$memberId' });
  const { fleet, role } = useActiveFleet();
  const fleetId = fleet?.id ?? null;

  const memberQuery = useDriverDetail(fleetId, memberId);

  if (memberQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (memberQuery.isError || !memberQuery.data) {
    return (
      <div className="px-8 py-10 max-w-[1100px]">
        <BackLink />
        <EmptyState
          title="Couldn&rsquo;t load member"
          description={(memberQuery.error as Error)?.message ?? 'Server error.'}
        />
      </div>
    );
  }

  const member = memberQuery.data;
  const isOwnerActor = role === 'owner';
  const isAdminActor = role === 'owner' || role === 'admin';
  const isDriverBadged = member.roles.includes('driver');
  const isOwnerRow = member.roles.includes('owner');

  return (
    <div className="px-8 py-10 max-w-[1100px]">
      <div className="mb-4">
        <BackLink />
      </div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-breaker-ink">
            {member.displayName}
          </h1>
          <p className="text-sm text-breaker-mute mt-1">
            @{member.handle}
            {member.joinedAt ? ` · joined ${formatDate(member.joinedAt)}` : ''}
          </p>
          <div className="mt-2">
            <BadgeChips roles={member.roles} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {member.status === 'active' ? (
            <Badge tone="success">Active</Badge>
          ) : (
            <Badge tone="danger">Disabled</Badge>
          )}
          {isAdminActor && !isOwnerRow ? (
            <DisableButton fleetId={fleetId} member={member} />
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-3 gap-6 items-start">
        <div className="col-span-2">
          {isAdminActor ? (
            <BadgesCard fleetId={fleetId} member={member} isOwnerActor={isOwnerActor} />
          ) : null}
          {isDriverBadged ? (
            <>
              <EngagementCard fleetId={fleetId} driver={member} />
              <ComplianceCard fleetId={fleetId} driver={member} />
              <OwnVehiclesCard fleetId={fleetId} driver={member} />
            </>
          ) : null}
          <EmploymentCard fleetId={fleetId} member={member} isOwnerActor={isOwnerActor} canEdit={isAdminActor} />
        </div>
        <div>
          <ContactCard driver={member} />
        </div>
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/settings/team"
      className="text-sm text-breaker-mute hover:text-breaker-identity transition-colors"
    >
      {'←'} Team
    </Link>
  );
}

/**
 * The badge toggles (Team unit). Owner is shown fixed, never interactive
 * (singular, transfer = future). Driver is the billed badge: toggling it on
 * shows "+£20/month" at the point of toggle and requires an engagement type
 * (prompted inline when unset). Admin is owner-only. The server enforces
 * the same matrix — this is presentation.
 */
function BadgesCard({
  fleetId,
  member,
  isOwnerActor,
}: {
  fleetId: string | null;
  member: DriverListEntry;
  isOwnerActor: boolean;
}) {
  const update = useUpdateDriver(fleetId);
  const toast = useToast();
  const [pendingEngagement, setPendingEngagement] = useState<DriverEngagementType>('employed');
  const [confirmingDriverOn, setConfirmingDriverOn] = useState(false);
  const isOwnerRow = member.roles.includes('owner');
  const held = new Set(member.roles);

  function saveRoles(nextRoles: InvitableBadge[], engagementType?: DriverEngagementType) {
    return update
      .mutateAsync({
        memberId: member.memberId,
        body: {
          roles: nextRoles,
          ...(engagementType ? { engagementType } : {}),
        },
      })
      .then(() => toast.success('Badges updated'))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Update failed'));
  }

  function toggle(badge: InvitableBadge, on: boolean) {
    const next = new Set(member.roles.filter((r): r is InvitableBadge => r !== 'owner'));
    if (on) next.add(badge);
    else next.delete(badge);
    if (!isOwnerRow && next.size === 0) {
      toast.error('A member needs at least one badge.');
      return Promise.resolve();
    }
    if (badge === 'driver' && on && !member.engagementType) {
      // Driver ON needs an engagement type — prompt inline instead of a 400.
      setConfirmingDriverOn(true);
      return Promise.resolve();
    }
    return saveRoles([...next]);
  }

  const rows: Array<{ badge: InvitableBadge; label: string; hint: string; ownerOnly?: boolean }> = [
    { badge: 'admin', label: 'Admin', hint: 'Edits fleet settings and runs the team.', ownerOnly: true },
    { badge: 'dispatcher', label: 'Dispatcher', hint: 'Schedules and assigns loads.' },
    { badge: 'accountant', label: 'Accountant', hint: 'Billing and invoices only.' },
    { badge: 'driver', label: 'Driver', hint: 'Operates loads. +£20/month while active.' },
  ];

  return (
    <Section title="Badges">
      <div className="space-y-3">
        {isOwnerRow ? (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-breaker-ink">Owner</p>
              <p className="mt-0.5 text-xs text-breaker-mute">
                One per fleet — can&rsquo;t be handed over or removed yet.
              </p>
            </div>
            <Badge tone="info">Held</Badge>
          </div>
        ) : null}
        {rows.map((r) => {
          const on = held.has(r.badge);
          const locked = Boolean(r.ownerOnly) && !isOwnerActor;
          return (
            <div key={r.badge} className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-breaker-ink">{r.label}</p>
                <p className="mt-0.5 text-xs text-breaker-mute">
                  {r.hint}
                  {locked ? ' Owner-only.' : ''}
                </p>
              </div>
              <input
                type="checkbox"
                className="h-4 w-4 mt-1"
                checked={on}
                disabled={locked || update.isPending}
                onChange={(ev) => void toggle(r.badge, ev.target.checked)}
              />
            </div>
          );
        })}
      </div>
      <Dialog
        open={confirmingDriverOn}
        onClose={() => setConfirmingDriverOn(false)}
        title="Add the Driver badge"
        description="Driver is the billed badge — +£20/month while their account is active. Choose how they're engaged first; subcontractors need cover on record before they can carry loads."
      >
        <div className="space-y-4">
          <Field label="Engagement" htmlFor="badgeEngagement">
            <select
              id="badgeEngagement"
              value={pendingEngagement}
              onChange={(e) => setPendingEngagement(e.target.value as DriverEngagementType)}
              className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm"
            >
              <option value="employed">Employed</option>
              <option value="subcontractor">Subcontractor</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmingDriverOn(false)}
              disabled={update.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              loading={update.isPending}
              action={() => {
                const next = new Set(
                  member.roles.filter((r): r is InvitableBadge => r !== 'owner'),
                );
                next.add('driver');
                return saveRoles([...next], pendingEngagement).then(() =>
                  setConfirmingDriverOn(false),
                );
              }}
            >
              Add Driver — +£20/month
            </Button>
          </div>
        </div>
      </Dialog>
    </Section>
  );
}

function EmploymentCard({
  fleetId,
  member,
  isOwnerActor,
  canEdit,
}: {
  fleetId: string | null;
  member: DriverListEntry;
  isOwnerActor: boolean;
  canEdit: boolean;
}) {
  const update = useUpdateDriver(fleetId);
  const toast = useToast();
  const [employeeRef, setEmployeeRef] = useState(member.employeeRef ?? '');
  const [costCentre, setCostCentre] = useState(member.costCentre ?? '');
  const [shiftPattern, setShiftPattern] = useState(member.shiftPattern ?? '');
  const [hourlyRate, setHourlyRate] = useState(
    member.hourlyRatePence !== null ? (member.hourlyRatePence / 100).toFixed(2) : '',
  );
  useEffect(() => {
    setEmployeeRef(member.employeeRef ?? '');
    setCostCentre(member.costCentre ?? '');
    setShiftPattern(member.shiftPattern ?? '');
    setHourlyRate(
      member.hourlyRatePence !== null ? (member.hourlyRatePence / 100).toFixed(2) : '',
    );
  }, [member.memberId, member.employeeRef, member.costCentre, member.shiftPattern, member.hourlyRatePence]);

  if (!canEdit) return null;

  function save() {
    const ratePence =
      hourlyRate.trim() === '' ? null : Math.round(Number(hourlyRate) * 100);
    if (ratePence !== null && (!Number.isFinite(ratePence) || ratePence < 0)) {
      toast.error('Hourly rate must be a positive amount.');
      return Promise.resolve();
    }
    return update
      .mutateAsync({
        memberId: member.memberId,
        body: {
          employeeRef: employeeRef.trim() || null,
          costCentre: costCentre.trim() || null,
          shiftPattern: shiftPattern.trim() || null,
          ...(isOwnerActor ? { hourlyRatePence: ratePence } : {}),
        },
      })
      .then(() => toast.success('Employment details saved'))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Save failed'));
  }

  return (
    <Section title="Employment">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Employee ref" htmlFor="empRef">
          <Input id="empRef" value={employeeRef} onChange={(e) => setEmployeeRef(e.target.value)} />
        </Field>
        <Field label="Cost centre" htmlFor="empCost">
          <Input id="empCost" value={costCentre} onChange={(e) => setCostCentre(e.target.value)} />
        </Field>
        <Field label="Shift pattern" htmlFor="empShift">
          <Input id="empShift" value={shiftPattern} onChange={(e) => setShiftPattern(e.target.value)} />
        </Field>
        <Field
          label="Hourly rate (£)"
          htmlFor="empRate"
          hint={isOwnerActor ? undefined : 'Owner-only.'}
        >
          <Input
            id="empRate"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            disabled={!isOwnerActor}
            placeholder="—"
          />
        </Field>
      </div>
      <div className="mt-4 flex justify-end">
        <Button size="sm" action={save} loading={update.isPending}>
          Save
        </Button>
      </div>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="surface rounded-lg p-5 mb-6">
      <h2 className="text-sm font-semibold text-breaker-ink mb-3">{title}</h2>
      {children}
    </section>
  );
}

function ContactCard({ driver }: { driver: DriverListEntry }) {
  return (
    <Section title="Contact">
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-2xs uppercase tracking-wider text-breaker-ink-2">Email</dt>
          <dd className="text-breaker-ink-2">{driver.email}</dd>
        </div>
        <div>
          <dt className="text-2xs uppercase tracking-wider text-breaker-ink-2">Mobile</dt>
          <dd className="text-breaker-ink-2">
            {driver.phoneE164 ?? <span className="text-breaker-mute">{'—'}</span>}
          </dd>
        </div>
        <div>
          <dt className="text-2xs uppercase tracking-wider text-breaker-ink-2">
            Platform verification
          </dt>
          <dd className="text-breaker-ink-2">
            {driver.verificationState === 'verified' ? (
              <Badge tone="success">Verified driver</Badge>
            ) : driver.verificationState === 'pending_review' ? (
              <Badge tone="info">Under review</Badge>
            ) : (
              <span className="text-breaker-mute">Not verified</span>
            )}
          </dd>
        </div>
      </dl>
    </Section>
  );
}

/** Disable-not-delete: the account keeps its history, frees the £20 seat,
 *  and can't sign in to this fleet until re-enabled. */
function DisableButton({
  fleetId,
  member,
}: {
  fleetId: string | null;
  member: DriverListEntry;
}) {
  const update = useUpdateDriver(fleetId);
  const toast = useToast();
  const disabling = member.status === 'active';
  return (
    <Button
      variant={disabling ? 'danger' : 'secondary'}
      size="sm"
      action={() =>
        update
          .mutateAsync({
            memberId: member.memberId,
            body: { status: disabling ? 'suspended' : 'active' },
          })
          .then(() =>
            toast.success(
              disabling
                ? 'Account disabled — history kept, the seat is freed'
                : 'Account re-enabled',
            ),
          )
          .catch((e) => toast.error(e instanceof Error ? e.message : 'Update failed'))
      }
    >
      {disabling ? 'Disable account' : 'Enable account'}
    </Button>
  );
}

function EngagementCard({
  fleetId,
  driver,
}: {
  fleetId: string | null;
  driver: DriverListEntry;
}) {
  const update = useUpdateDriver(fleetId);
  const vehiclesQuery = useVehicles(fleetId);
  const toast = useToast();
  const [engagement, setEngagement] = useState<DriverEngagementType | ''>(
    driver.engagementType ?? '',
  );
  const [defaultVehicleId, setDefaultVehicleId] = useState<string>(
    driver.defaultVehicle?.id ?? '',
  );
  useEffect(() => {
    setEngagement(driver.engagementType ?? '');
    setDefaultVehicleId(driver.defaultVehicle?.id ?? '');
  }, [driver.memberId, driver.engagementType, driver.defaultVehicle?.id]);

  // A default can be a pool vehicle or one of this driver's own — never
  // another driver's vehicle (the server enforces the same rule).
  const eligible = (vehiclesQuery.data ?? []).filter(
    (v) => v.isActive && (!v.ownerDriverUserId || v.ownerDriverUserId === driver.userId),
  );

  const dirty =
    engagement !== (driver.engagementType ?? '') ||
    defaultVehicleId !== (driver.defaultVehicle?.id ?? '');

  function save() {
    return update
      .mutateAsync({
        memberId: driver.memberId,
        body: {
          engagementType: engagement === '' ? null : engagement,
          defaultVehicleId: defaultVehicleId === '' ? null : defaultVehicleId,
        },
      })
      .then(() => toast.success('Driver updated'))
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Update failed'));
  }

  return (
    <Section title="Engagement & default vehicle">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Engagement" htmlFor="dEngagement">
          <select
            id="dEngagement"
            value={engagement}
            onChange={(e) => setEngagement(e.target.value as DriverEngagementType | '')}
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm"
          >
            <option value="">Not set</option>
            <option value="subcontractor">Subcontractor</option>
            <option value="employed">Employed</option>
          </select>
        </Field>
        <Field label="Default vehicle" htmlFor="dVehicle">
          <select
            id="dVehicle"
            value={defaultVehicleId}
            onChange={(e) => setDefaultVehicleId(e.target.value)}
            className="w-full rounded-md border border-breaker-line bg-white px-3 py-2 text-sm"
          >
            <option value="">None</option>
            {eligible.map((v) => (
              <option key={v.id} value={v.id}>
                {v.registration} · {vehicleTypeLabel(v.type)}
                {v.ownerDriverUserId === driver.userId ? ' (own)' : ' (pool)'}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <p className="text-xs text-breaker-mute mt-3">
        The default vehicle lands on each load this driver is assigned, switchable per job
        from the load page. Subcontractors need valid cover below before they can be
        assigned anything.
      </p>
      <div className="mt-4 flex justify-end">
        <Button size="sm" action={save} disabled={!dirty}>
          Save
        </Button>
      </div>
    </Section>
  );
}

function ComplianceCard({
  fleetId,
  driver,
}: {
  fleetId: string | null;
  driver: DriverListEntry;
}) {
  const complianceQuery = useDriverCompliance(fleetId, driver.memberId);
  const docs = complianceQuery.data?.documents ?? [];
  const state = complianceQuery.data?.state;

  return (
    <Section title="Compliance — goods in transit & public liability">
      {complianceQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            {state ? <ComplianceBadge state={state} /> : null}
            <p className="text-xs text-breaker-mute">
              {driver.engagementType === 'subcontractor'
                ? 'Assignment is blocked while either cover is missing or expired.'
                : 'Recorded for reference — only subcontractors are gated.'}
            </p>
          </div>
          <div className="space-y-4">
            {COMPLIANCE_TYPES.map(({ type, label }) => (
              <ComplianceRow
                key={type}
                fleetId={fleetId}
                memberId={driver.memberId}
                type={type}
                label={label}
                document={docs.find((d) => d.type === type) ?? null}
              />
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

function ComplianceRow({
  fleetId,
  memberId,
  type,
  label,
  document,
}: {
  fleetId: string | null;
  memberId: string;
  type: DriverComplianceDocType;
  label: string;
  document: DriverComplianceDocumentWithUrl | null;
}) {
  const upsert = useUpsertDriverCompliance(fleetId, memberId);
  const remove = useRemoveDriverCompliance(fleetId, memberId);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pending = document?.reviewState === 'pending';
  const [reference, setReference] = useState(document?.reference ?? '');
  const [insurer, setInsurer] = useState(document?.insurer ?? '');
  const [expiresAt, setExpiresAt] = useState(document?.expiresAt ?? '');

  useEffect(() => {
    setReference(document?.reference ?? '');
    setInsurer(document?.insurer ?? '');
    setExpiresAt(document?.expiresAt ?? '');
  }, [document?.id, document?.reference, document?.insurer, document?.expiresAt]);

  function save() {
    if (!expiresAt) {
      toast.error('An expiry date is required.');
      return Promise.resolve();
    }
    return upsert
      .mutateAsync({
        type,
        body: {
          expiresAt,
          reference: reference.trim() || null,
          insurer: insurer.trim() || null,
        },
      })
      .then(() => {
        toast.success(`${label} cover recorded`);
        setEditing(false);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Save failed'));
  }

  return (
    <div className="border border-breaker-line rounded-md p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-breaker-ink">{label}</span>
          {pending ? (
            <Badge tone="active">Uploaded — pending your check</Badge>
          ) : null}
          {document && document.expiresAt ? (
            document.status === 'expired' ? (
              <Badge tone="danger">Expired {formatDate(document.expiresAt)}</Badge>
            ) : document.status === 'expiring_soon' ? (
              <Badge tone="info">Expires {formatDate(document.expiresAt)}</Badge>
            ) : (
              <Badge tone="success">Valid to {formatDate(document.expiresAt)}</Badge>
            )
          ) : !pending ? (
            <Badge tone="neutral">Not on record</Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {document?.url ? (
            <a
              href={document.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-breaker-ink-2 underline underline-offset-2"
            >
              View file
            </a>
          ) : null}
          {pending ? (
            <Button size="sm" onClick={() => setConfirming(true)}>
              Confirm
            </Button>
          ) : null}
          {document && !editing ? (
            <Button
              variant="ghost"
              size="sm"
              action={() =>
                remove
                  .mutateAsync(type)
                  .then(() => toast.success(`${label} record removed`))
                  .catch((e) =>
                    toast.error(e instanceof Error ? e.message : 'Remove failed'),
                  )
              }
            >
              Remove
            </Button>
          ) : null}
          {!editing ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {document ? 'Renew' : 'Record'}
            </Button>
          ) : null}
        </div>
      </div>
      {document && !editing ? (
        <p className="text-xs text-breaker-mute mt-2">
          {[document.insurer, document.reference].filter(Boolean).join(' · ') ||
            'No policy details recorded.'}
        </p>
      ) : null}
      {editing ? (
        <div className="mt-4 grid grid-cols-3 gap-3 items-end">
          <Field label="Insurer" htmlFor={`${type}-insurer`}>
            <Input
              id={`${type}-insurer`}
              value={insurer}
              onChange={(e) => setInsurer(e.target.value)}
            />
          </Field>
          <Field label="Policy ref" htmlFor={`${type}-ref`}>
            <Input
              id={`${type}-ref`}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
          <Field label="Expires" htmlFor={`${type}-expires`}>
            <Input
              id={`${type}-expires`}
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </Field>
          <div className="col-span-3 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={upsert.isPending}
            >
              Cancel
            </Button>
            <Button size="sm" action={save} loading={upsert.isPending}>
              Save
            </Button>
          </div>
        </div>
      ) : null}
      {document ? (
        <ConfirmUploadDialog
          open={confirming}
          onClose={() => setConfirming(false)}
          fleetId={fleetId}
          memberId={memberId}
          type={type}
          label={label}
          document={document}
        />
      ) : null}
    </div>
  );
}

/**
 * V2-U6: the fleet's check of a driver upload. Look at the file, type
 * what it evidences — the confirm endpoint records the dates and flips
 * the row (and the driver's chip) from "pending fleet check" to a real
 * Valid-until.
 */
function ConfirmUploadDialog({
  open,
  onClose,
  fleetId,
  memberId,
  type,
  label,
  document,
}: {
  open: boolean;
  onClose: () => void;
  fleetId: string | null;
  memberId: string;
  type: DriverComplianceDocType;
  label: string;
  document: DriverComplianceDocumentWithUrl;
}) {
  const confirm = useConfirmDriverCompliance(fleetId, memberId);
  const toast = useToast();
  const [expiresAt, setExpiresAt] = useState('');
  const [insurer, setInsurer] = useState(document.insurer ?? '');
  const [reference, setReference] = useState(document.reference ?? '');

  function submit() {
    if (!expiresAt) {
      toast.error('An expiry date is required.');
      return Promise.resolve();
    }
    return confirm
      .mutateAsync({
        type,
        body: {
          expiresAt,
          insurer: insurer.trim() || null,
          reference: reference.trim() || null,
        },
      })
      .then(() => {
        toast.success(`${label} upload confirmed`);
        onClose();
      })
      .catch((e) =>
        toast.error(e instanceof Error ? e.message : 'Confirm failed'),
      );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Confirm ${label.toLowerCase()} upload`}
      description={`The driver uploaded ${document.fileName ?? 'a certificate'}. Check the file, then record what it evidences — the dates come from you, not the photo.`}
    >
      <div className="space-y-3">
        {document.url ? (
          <a
            href={document.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-breaker-ink-2 underline underline-offset-2"
          >
            View uploaded file
          </a>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expires" htmlFor={`${type}-confirm-expires`}>
            <Input
              id={`${type}-confirm-expires`}
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </Field>
          <Field label="Insurer" htmlFor={`${type}-confirm-insurer`}>
            <Input
              id={`${type}-confirm-insurer`}
              value={insurer}
              onChange={(e) => setInsurer(e.target.value)}
            />
          </Field>
          <Field label="Policy ref" htmlFor={`${type}-confirm-ref`}>
            <Input
              id={`${type}-confirm-ref`}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={confirm.isPending}>
            Cancel
          </Button>
          <Button size="sm" action={submit} loading={confirm.isPending}>
            Confirm upload
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function OwnVehiclesCard({
  fleetId,
  driver,
}: {
  fleetId: string | null;
  driver: DriverListEntry;
}) {
  const vehiclesQuery = useVehicles(fleetId);
  const own = (vehiclesQuery.data ?? []).filter(
    (v) => v.ownerDriverUserId === driver.userId,
  );

  return (
    <Section title="Own vehicles">
      {vehiclesQuery.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : own.length === 0 ? (
        <p className="text-sm text-breaker-mute">
          No driver-owned vehicles. Add one on the{' '}
          <Link to="/vehicles" className="text-breaker-identity hover:underline">
            Vehicles
          </Link>{' '}
          page and set its owner to this driver.
        </p>
      ) : (
        <ul className="space-y-2 text-sm">
          {own.map((v) => (
            <li key={v.id} className="flex items-center gap-3">
              <span className="ref">{v.registration}</span>
              <span className="text-breaker-ink-2">{vehicleTypeLabel(v.type)}</span>
              {[v.make, v.model].filter(Boolean).length > 0 ? (
                <span className="text-2xs text-breaker-mute">
                  {[v.make, v.model].filter(Boolean).join(' ')}
                </span>
              ) : null}
              {driver.defaultVehicle?.id === v.id ? (
                <Badge tone="info">Default</Badge>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
